import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { withMemberGate } from "@/lib/with-member-gate";
import { useTriggerJournal } from "@/components/trigger-journal/useTriggerJournal";
import { TriggerEntryEditor } from "@/components/trigger-journal/TriggerEntryEditor";
import { TriggerMap } from "@/components/trigger-journal/TriggerMap";
import { TriggerCardsPanel } from "@/components/trigger-journal/TriggerCards";
import { EntryList } from "@/components/trigger-journal/EntryList";
import type { TriggerEntry } from "@/lib/trigger-journal";
import { JournalGuide } from "@/components/JournalGuide";
import { accentAt } from "@/lib/journal-accents";

export const Route = createFileRoute("/trigger-journal")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Trigger Journal — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A private, trauma-informed Trigger Journal: record what happened, how it felt in your body, what you needed, and which tools helped — then see your own patterns in My Trigger Map.",
      },
      { property: "og:title", content: "Trigger Journal — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Record triggers gently and privately, build quick-reference Trigger Cards, and watch your own patterns emerge over time.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/trigger-journal" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/trigger-journal" }],
  }),
  component: withMemberGate(TriggerJournalPage, "Trigger Journal"),
});

type Tab = "journal" | "map" | "cards";

const TABS: { key: Tab; label: string }[] = [
  { key: "journal", label: "Journal" },
  { key: "map", label: "My Trigger Map" },
  { key: "cards", label: "Trigger Cards" },
];

function TriggerJournalPage() {
  const journal = useTriggerJournal();
  const [tab, setTab] = useState<Tab>("journal");
  const [editing, setEditing] = useState<TriggerEntry | null | "new">(null);

  return (
    <div className="min-h-screen pb-24">
      <div className="mx-auto max-w-4xl px-4 pt-6 sm:px-6">

        <header
          className="mt-4 rounded-[26px] border border-border/60 p-5 shadow-sm sm:p-7"
          style={{
            backgroundImage:
              "linear-gradient(150deg, var(--qa-rose-tint) 0%, var(--qa-peach-tint) 48%, var(--card) 100%)",
          }}
        >
          <p
            className="inline-flex items-center gap-2 rounded-full bg-card px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: "var(--qa-rose-ink)" }}
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden /> Private · Trigger Journal
          </p>
          <h1 className="mt-3 font-display text-[2rem] leading-tight text-foreground sm:text-5xl">
            Your Trigger Journal
          </h1>
          <p className="mt-3 text-[1.02rem] leading-relaxed text-muted-foreground">
            A trigger isn&apos;t an overreaction — it&apos;s a nervous system doing what it
            learned to do to keep you safe. This space is for noticing, gently and privately.
            Fill in as much or as little as you want, and stop at any point.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Everything here is yours alone. It is never shared, never published and never used
            to assess or diagnose you.
          </p>
        </header>
        <JournalGuide journal="trigger" className="mt-6" defaultOpen />

        <nav
          className="mt-6 -mx-4 flex snap-x snap-mandatory gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0"
          aria-label="Trigger Journal sections"
        >
          {TABS.map((t, i) => {
            const a = accentAt(i + 3);
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                aria-current={active ? "page" : undefined}
                className={`min-h-[44px] shrink-0 snap-start whitespace-nowrap rounded-full px-4 text-sm transition ${
                  active ? "font-semibold shadow-sm" : ""
                }`}
                style={{
                  backgroundColor: a.tint,
                  color: a.ink,
                  boxShadow: active ? `inset 0 0 0 2px ${a.ink}` : `inset 0 0 0 1px ${a.tint}`,
                  opacity: active ? 1 : 0.75,
                }}
              >
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="mt-8">
          {journal.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading your journal…</p>
          ) : tab === "journal" ? (
            editing !== null ? (
              <TriggerEntryEditor
                entry={editing === "new" ? null : editing}
                saving={journal.isSaving}
                onCancel={() => setEditing(null)}
                onSave={async (draft) => {
                  await journal.saveEntry(draft);
                  toast.success("Saved — it will be here whenever you come back.");
                  setEditing(null);
                }}
              />
            ) : (
              <div className="space-y-6">
                <button
                  type="button"
                  onClick={() => setEditing("new")}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[var(--gold-deep)] px-5 text-sm font-semibold text-white"
                >
                  <Plus className="h-4 w-4" aria-hidden /> Record a trigger
                </button>
                <EntryList
                  entries={journal.entries}
                  onEdit={(e) => setEditing(e)}
                  onDelete={journal.deleteEntry}
                />
              </div>
            )
          ) : tab === "map" ? (
            <TriggerMap entries={journal.entries} />
          ) : (
            <TriggerCardsPanel
              cards={journal.cards}
              saving={journal.isSavingCard}
              onSave={async (draft) => {
                await journal.saveCard(draft);
                toast.success("Card saved.");
              }}
              onDelete={journal.deleteCard}
            />
          )}
        </div>
      </div>
    </div>
  );
}
